import { parseUrl } from 'query-string';
import { ParsedUrlQuery } from 'querystring';

import { ESearchFilters } from '@/constants/searchFilters';
import { OptimizelyFlags } from '@/services/experiments';

export interface IPrecedingSearchData {
  address?: string;
  guests?: number;
  dateFrom?: string;
  dateTo?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  otherFilters?: Record<string, any>;
  experiments?: string[];
  searchResultOrder?: number; // (0 to 23)
  pageNumber?: number;
}

const filterValue = (v: unknown) =>
  // eslint-disable-next-line no-nested-ternary
  typeof v === 'string' ? v : Array.isArray(v) ? v[0] : undefined;

export const getPrecedingSearchData = (
  searchUrl: string | undefined,
  pageQueryParams: ParsedUrlQuery,
): IPrecedingSearchData => {
  const { query: queryParams } = searchUrl ? parseUrl(searchUrl) : { query: {} as ParsedUrlQuery };
  const {
    [ESearchFilters.ADDRESS]: rawAddress,
    [ESearchFilters.GEOIP_ADDRESS]: rawGeoipAddress,
    [ESearchFilters.GUESTS_ADULTS]: rawAdultsGuests,
    [ESearchFilters.GUESTS_CHILDREN]: rawChildrenGuests,
    [ESearchFilters.DATE_FROM]: rawDateFrom,
    [ESearchFilters.DATE_TO]: rawDateTo,
    [ESearchFilters.PAGE_OFFSET]: pageOffset,
    [ESearchFilters.PAGE_LIMIT]: pageLimit,
    ...searchQueryFilters
  } = queryParams;

  const {
    [ESearchFilters.ADDRESS]: rawAddressPageQuery,
    [ESearchFilters.RESULT_NUMBER]: rawResultNumberPageQuery,
    [ESearchFilters.DATE_FROM]: rawDateFromPageQuery,
    [ESearchFilters.DATE_TO]: rawDateToPageQuery,
    [ESearchFilters.SLEEPS_ADULTS]: rawSleepsAdultsPageQuery,
    [ESearchFilters.SLEEPS_CHILDREN]: rawSleepsChildrenPageQuery,
    ...queryFilters
  } = pageQueryParams;

  const otherQuery = {
    ...searchQueryFilters,
    ...queryFilters,
  };

  const address = filterValue(rawAddress || rawAddressPageQuery || rawGeoipAddress);
  const childrenGuests = parseInt(
    filterValue(rawChildrenGuests || rawSleepsChildrenPageQuery) || '0',
  );
  const adultsGuests = parseInt(filterValue(rawAdultsGuests || rawSleepsAdultsPageQuery) || '0');
  const dateFrom = filterValue(rawDateFrom) || filterValue(rawDateFromPageQuery);
  const dateTo = filterValue(rawDateTo) || filterValue(rawDateToPageQuery);
  const offset = Number(pageOffset) || 0;
  const limit = Number(pageLimit) || 0;
  const searchResultOrder = Number(rawResultNumberPageQuery) || undefined;

  const pageNumber = offset && limit ? Math.floor(offset / limit) + 1 : 1;

  const optimizelyFlagKeys = Object.values(OptimizelyFlags);

  const experiments = optimizelyFlagKeys.filter(flagKey => otherQuery[flagKey] === 'true');

  // Remove experiment flags from otherQuery
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const otherFilters = Object.keys(otherQuery).reduce<Record<string, any>>((acc, key) => {
    if (!optimizelyFlagKeys.includes(key as never)) {
      acc[key] = otherQuery[key];
    }
    return acc;
  }, {});

  return {
    address,
    guests: childrenGuests + adultsGuests,
    dateFrom: dateFrom ? new Date(dateFrom).toISOString() : undefined,
    dateTo: dateTo ? new Date(dateTo).toISOString() : undefined,
    otherFilters,
    experiments: experiments,
    // Only include these values if the user navigated from the SRP; otherwise, omit them to avoid incorrect data
    searchResultOrder: searchUrl ? searchResultOrder : undefined,
    pageNumber: searchUrl ? pageNumber : undefined,
  };
};
